import { useState, useCallback } from 'react';
import { Mold, MoldCreationData } from '../types/mold';
import { moldService } from '../services/api';
import { getNestedValue, getUpdatedMold } from '../utils/object';
import { getAppState } from '../models/appState';
import { resolveClientCompanyCode } from '../utils/company';

interface FieldErrorDetail {
  Field: string;
  Msg: string;
}

interface UseMoldState {
  mold: Mold | null;
  editedMold: Mold | null;
  isEditing: boolean;
  loading: boolean;
  error: string | null;
  fieldErrors: Record<string, boolean>;
}

const moldKeyFields = new Set(['moldCode', 'moldName']);

const requiredEditableMoldKeyFields = () => {
  const appState = getAppState(resolveClientCompanyCode());
  return Object.values(appState.mold.formConfiguration)
    .flat()
    .filter((field) => field.required && !field.disabled && moldKeyFields.has(String(field.key)))
    .map((field) => String(field.key));
};

const isBlankRequiredValue = (value: unknown) => (
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '')
);

const validateRequiredEditableFields = (mold: Mold): Record<string, boolean> => {
  return requiredEditableMoldKeyFields().reduce<Record<string, boolean>>((errors, field) => {
    if (isBlankRequiredValue(getNestedValue(mold, field))) {
      errors[field] = true;
    }
    return errors;
  }, {});
};

export const useMold = (initialMold?: Mold) => {
  const [state, setState] = useState<UseMoldState>({
    mold: initialMold || null,
    editedMold: initialMold || null,
    isEditing: false,
    loading: false,
    error: null,
    fieldErrors: {},
  });

  const setPartialState = (newState: Partial<UseMoldState>) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  const fetchMold = useCallback(async (id: string) => {
    setPartialState({ loading: true, error: null });
    try {
      const response = await moldService.getMoldById(id);
      setPartialState({
        mold: response.data,
        editedMold: response.data,
        loading: false,
      });
    } catch (err) {
      setPartialState({
        error:
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching the mold',
        loading: false,
      });
    }
  }, []);

  const handleEditMode = useCallback(() => {
    setPartialState({
      isEditing: true,
      editedMold: state.mold,
    });
  }, [state.mold]);

  const handleCancelEdit = useCallback(() => {
    setPartialState({
      isEditing: false,
      editedMold: state.mold,
      error: null,
      fieldErrors: {},
    });
  }, [state.mold]);

  const handleSaveEdit = useCallback(async () => {
    if (!state.editedMold) return;

    const localFieldErrors = validateRequiredEditableFields(state.editedMold);
    if (Object.keys(localFieldErrors).length > 0) {
      setPartialState({
        error: 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน',
        fieldErrors: localFieldErrors,
        loading: false,
      });
      return;
    }

    setPartialState({ loading: true, error: null, fieldErrors: {} });
    try {
      const response = await moldService.updateMold(state.editedMold.id, state.editedMold);
      const updatedMold = response.data;
      setPartialState({
        // PATCH redirects to the canonical GET response. Keep it so derived fields
        // such as SK's sbiId stay in sync with a changed moldCode.
        mold: updatedMold,
        editedMold: updatedMold,
        isEditing: false,
        loading: false,
      });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: FieldErrorDetail[] } } })
        ?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const fieldErrors: Record<string, boolean> = {};
        const errorMessages: string[] = [];
        detail.forEach((fe) => {
          if (fe.Field) {
            fieldErrors[fe.Field] = true;
            errorMessages.push(`${fe.Field}: ${fe.Msg}`);
          }
        });
        setPartialState({
          fieldErrors,
          error: `กรุณาตรวจสอบข้อมูล: ${errorMessages.join(', ')}`,
          loading: false,
        });
      } else {
        setPartialState({
          error: 'ไม่สามารถอัพเดทข้อมูลแม่พิมพ์ได้ กรุณาลองใหม่อีกครั้ง',
          loading: false,
        });
      }
    }
  }, [state.editedMold]);

  const handleFieldChange = useCallback(
    (field: keyof Mold, value: any) => {
      setState((prev) => {
        const fieldKey = field as string;
        const updatedFieldErrors = prev.fieldErrors[fieldKey]
          ? { ...prev.fieldErrors, [fieldKey]: false }
          : prev.fieldErrors;
        return {
          ...prev,
          editedMold: getUpdatedMold(prev.editedMold || {}, field, value) as Mold,
          fieldErrors: updatedFieldErrors,
        };
      });
    },
    [state.editedMold],
  );

  const associatePart = useCallback(async (moldId: string, partId: number) => {
    setPartialState({ loading: true, error: null });
    try {
      await moldService.associatePart(moldId, partId);
      setPartialState({ loading: false });
    } catch (err) {
      setPartialState({
        error: 'Failed to associate part. Please try again.',
        loading: false,
      });
    }
  }, []);
  
  const associateInsert = useCallback(async (moldId: string, insertId: number) => {
    setPartialState({ loading: true, error: null });
    try {
      await moldService.associateInsert(moldId, insertId);
      setPartialState({ loading: false });
    } catch (err) {
      setPartialState({
        error: 'Failed to associate part. Please try again.',
        loading: false,
      });
    }
  }, []);

  const deleteAssociatedPart = useCallback(async (moldId: string, partId: number) => {
    setPartialState({ loading: true, error: null });
    try {
      await moldService.deleteAssociatedPart(moldId, partId);
      setPartialState({ loading: false });
    } catch (err) {
      setPartialState({
        error: 'Failed to delete associated part. Please try again.',
        loading: false,
      });
    }
  }, []);

  const deleteAssociatedInsert = useCallback(async (moldId: string, insertId: number) => {
    setPartialState({ loading: true, error: null });
    try {
      await moldService.deleteAssociatedInsert(moldId, insertId);
      setPartialState({ loading: false });
    } catch (err) {
      setPartialState({
        error: 'Failed to delete associated insert. Please try again.',
        loading: false,
      });
    }
  }, []);

  const createMold = async (moldData: MoldCreationData) => {
    setPartialState({ loading: true, error: null });
    try {
      const response = await moldService.createMold(moldData);
      return response;
    } catch (err) {
      setPartialState({
        error: err instanceof Error ? err.message : 'ไม่สามารถสร้างแม่พิมพ์ได้ กรุณาลองใหม่อีกครั้ง',
        loading: false,
      });
      // do not rethrow; caller can rely on returned null or state.error
      return null;
    } finally {
      setPartialState({ loading: false });
    }
  };

  return {
    ...state,
    fetchMold,
    handleEditMode,
    handleCancelEdit,
    handleSaveEdit,
    handleFieldChange,
    associatePart,
    deleteAssociatedPart,
    associateInsert,
    deleteAssociatedInsert,
    createMold,
    fieldErrors: state.fieldErrors,
  };
};
